import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { AcceleratorCard } from "~/components/accelerator/accelerator-card";
import { AcceleratorJoinLink } from "~/components/accelerator/accelerator-join-link";
import { CoursePage } from "~/components/learning/course-page";
import { acceleratorQueryOptions } from "~/lib/queries/acceleratorQuery";
import { myCourseQueryOptions } from "~/lib/queries/learningQuery";
import { courseSlugSchema } from "~/lib/shared/learning/admin-inputs";
import { hasAcceleratorCard } from "~/lib/shared/learning/business-builder-outline";

export const Route = createFileRoute(
  "/_user/my-coaching/business/courses/$courseSlug/",
)({
  staticData: { title: "Course" },
  // Same title as the layout, so the breadcrumb collapses into one segment.
  loader: async ({ context, params }) => {
    if (!courseSlugSchema.safeParse(params.courseSlug).success) {
      throw notFound();
    }
    const [course] = await Promise.all([
      context.queryClient.ensureQueryData(
        myCourseQueryOptions(params.courseSlug),
      ),
      // The card is an addition to the page: a failed read leaves it out.
      hasAcceleratorCard(params.courseSlug)
        ? context.queryClient
            .ensureQueryData(acceleratorQueryOptions())
            .catch(() => undefined)
        : undefined,
    ]);
    if (!course) throw notFound();
    return { title: course.title };
  },
  component: CourseRoute,
});

function CourseRoute() {
  const { courseSlug } = Route.useParams();
  const { data: course } = useSuspenseQuery(myCourseQueryOptions(courseSlug));
  if (!course) throw notFound();
  const accelerator = hasAcceleratorCard(course.slug);
  return (
    <CoursePage
      course={course}
      leadLink={accelerator && <AcceleratorJoinLink />}
    >
      {accelerator && <AcceleratorCard />}
    </CoursePage>
  );
}
